"use client";

import AIFilterManagement from "@/components/library/AIFilterManagement";
import ClipArtManagement from "@/components/library/ClipArtManagement";
import FontManagement from "@/components/library/FontManagement";
import { AIFilter } from "@/interfaces/ai-filter.interface";
import { ClipArt, ClipArtCategory } from "@/interfaces/clip-art.interface";
import { Font } from "@/interfaces/font.interface";
import { Check } from "lucide-react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

interface Props {
  selectClipArtCategory: () => Promise<ClipArtCategory | undefined>;
  selectClipArt: () => Promise<ClipArt | undefined>;
  selectFont: () => Promise<Font | undefined>;
  selectAIFilter: () => Promise<AIFilter | undefined>;
}

const LibraryContext = createContext<Props>({
  selectClipArtCategory: () => Promise.resolve(undefined),
  selectClipArt: () => Promise.resolve(undefined),
  selectFont: () => Promise.resolve(undefined),
  selectAIFilter: () => Promise.resolve(undefined),
});

enum SelectingType {
  ClipArt = "clip-art",
  ClipArtCategory = "clip-art-category",
}

export default function LibraryProvider({
  children,
}: {
  children?: ReactNode;
}) {
  // clip art category ---------------------------------------------------
  const [selectingType, setSelectingType] = useState<SelectingType>();

  const [selectedClipArtCategory, setSelectedClipArtCategory] =
    useState<ClipArtCategory>();
  const selectClipArtCategoryPromise =
    useRef<(clipArtCategory: ClipArtCategory | undefined) => void>(null);

  const startSelectingClipArtCategory = async () => {
    setSelectingType(SelectingType.ClipArtCategory);
    setOpenClipArtPicker(true);
    return new Promise<ClipArtCategory | undefined>((resolve) => {
      selectClipArtCategoryPromise.current = resolve;
    });
  };

  const onSelectClipArtCategory = () => {
    selectClipArtCategoryPromise.current?.(selectedClipArtCategory);
    setOpenClipArtPicker(false);
  };

  // clip art ------------------------------------------------------------
  const [openClipArtPicker, setOpenClipArtPicker] = useState(false);
  const selectClipArtPromise =
    useRef<(clipArt: ClipArt | undefined) => void>(null);

  const startSelectingClipArt = async () => {
    setSelectingType(SelectingType.ClipArt);
    setOpenClipArtPicker(true);
    return new Promise<ClipArt | undefined>((resolve) => {
      selectClipArtPromise.current = resolve;
    });
  };

  const onSelectClipArt = (clipArt?: ClipArt) => {
    selectClipArtPromise.current?.(clipArt);
    setOpenClipArtPicker(false);
  };

  useEffect(() => {
    if (!openClipArtPicker) {
      selectClipArtPromise.current?.(undefined);
      setSelectedClipArtCategory(undefined);
    }
  }, [openClipArtPicker]);

  // font ----------------------------------------------------------------
  const [openFontPicker, setOpenFontPicker] = useState(false);
  const selectFontPromise = useRef<(font: Font | undefined) => void>(null);

  const startSelectingFont = async () => {
    setOpenFontPicker(true);
    return new Promise<Font | undefined>((resolve) => {
      selectFontPromise.current = resolve;
    });
  };

  const onSelectFont = (font?: Font) => {
    selectFontPromise.current?.(font);
    setOpenFontPicker(false);
  };

  useEffect(() => {
    if (!openFontPicker) {
      selectFontPromise.current?.(undefined);
    }
  }, [openFontPicker]);

  // ai filter -----------------------------------------------------------
  const [openAIFilterPicker, setOpenAIFilterPicker] = useState(false);
  const selectAIFilterPromise =
    useRef<(filter: AIFilter | undefined) => void>(null);

  const startSelectingAIFilter = async () => {
    setOpenAIFilterPicker(true);
    return new Promise<AIFilter | undefined>((resolve) => {
      selectAIFilterPromise.current = resolve;
    });
  };

  const onSelectAIFilter = (filter?: AIFilter) => {
    selectAIFilterPromise.current?.(filter);
    setOpenAIFilterPicker(false);
  };

  useEffect(() => {
    if (!openAIFilterPicker) {
      selectAIFilterPromise.current?.(undefined);
    }
  }, [openAIFilterPicker]);

  return (
    <LibraryContext.Provider
      value={{
        selectClipArtCategory: startSelectingClipArtCategory,
        selectClipArt: startSelectingClipArt,
        selectFont: startSelectingFont,
        selectAIFilter: startSelectingAIFilter,
      }}
    >
      {/* Clip art ---------------------------------------------------*/}
      <Dialog
        header="Clip arts"
        visible={openClipArtPicker}
        className="dialog"
        style={{ width: "70vw", height: "70vh" }}
        headerStyle={{ padding: 16 }}
        contentClassName="border-t-1 border-gray-300"
        contentStyle={{ padding: 0 }}
        footer={
          selectingType === SelectingType.ClipArtCategory ? (
            <div className="flex items-center justify-end gap-4 border-t-1 border-gray-300 p-4">
              <div className="text-md font-bold">
                {selectedClipArtCategory?.name || "Select a category"}
              </div>
              <Button
                severity="info"
                style={{
                  height: 36,
                  margin: 0,
                  padding: "6px 10px",
                  gap: 4,
                }}
                label="Select"
                tooltip="Select"
                tooltipOptions={{ position: "top" }}
                disabled={!selectedClipArtCategory}
                icon={<Check size={18} />}
                onClick={onSelectClipArtCategory}
              />
            </div>
          ) : undefined
        }
        onHide={() => setOpenClipArtPicker(false)}
      >
        <ClipArtManagement
          onSelectCategory={
            selectingType === SelectingType.ClipArtCategory
              ? setSelectedClipArtCategory
              : undefined
          }
          onSelectClipArt={
            selectingType === SelectingType.ClipArt
              ? onSelectClipArt
              : undefined
          }
        />
      </Dialog>
      {/* Font ------------------------------------------------------*/}
      <Dialog
        header="Fonts"
        visible={openFontPicker}
        className="dialog"
        style={{ width: "70vw", height: "70vh" }}
        headerStyle={{ padding: 16 }}
        contentClassName="border-t-1 border-gray-300"
        contentStyle={{ padding: 0 }}
        onHide={() => setOpenFontPicker(false)}
      >
        <FontManagement onSelectFont={onSelectFont} />
      </Dialog>
      {/* AI filter ---------------------------------------------------*/}
      <Dialog
        header="AI Filters"
        visible={openAIFilterPicker}
        className="dialog"
        style={{ width: "70vw", height: "70vh" }}
        headerStyle={{ padding: 16 }}
        contentClassName="border-t-1 border-gray-300"
        contentStyle={{ padding: 0 }}
        onHide={() => setOpenAIFilterPicker(false)}
      >
        <AIFilterManagement onSelectFilter={onSelectAIFilter} />
      </Dialog>
      {children}
    </LibraryContext.Provider>
  );
}

export const useLibrary = () => useContext(LibraryContext);
